import React, { useEffect, useState } from 'react';
import {
  useRecordConsentMutation,
  useLazyGetConsentRecordsQuery,
  ConsentRecord,
} from '../../features/apis/complianceApi';
import useCurrentUser from '../../customHook/useCurrentUser';
import { CONSENT_VERSION } from '../../util/constants';

const CONSENT_TYPES = [
  { value: 'data_processing', label: 'Data processing' },
  { value: 'marketing_emails', label: 'Marketing emails' },
  { value: 'third_party_sharing', label: 'Third-party sharing' },
];

function Consent({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const user = useCurrentUser();
  const [recordConsent, { isLoading: isSaving }] = useRecordConsentMutation();
  const [getConsentRecords, { data: records, isFetching, isError }] =
    useLazyGetConsentRecordsQuery();

  const [pendingType, setPendingType] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user?._id) {
      getConsentRecords({ userId: user._id });
    }
  }, [isOpen, user?._id, getConsentRecords]);

  useEffect(() => {
    if (!isOpen) {
      setFeedback(null);
      setPendingType(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setIsOpen]);

  // If every consent type is already signed for the current version
  // (e.g. re-fetched on a later mount), sync the localStorage flag and
  // don't force the modal to stay open.
  useEffect(() => {
    if (!records) return;
    const allSigned = CONSENT_TYPES.every((t) => {
      const record = records.find((r) => r.consentType === t.value);
      return Boolean(
        record?.given &&
          !record?.withdrawnAt &&
          record?.version === CONSENT_VERSION
      );
    });
    if (allSigned) {
      localStorage.setItem('consentAccepted', 'true');
    } else {
      localStorage.removeItem('consentAccepted');
    }
  }, [records]);

  if (!isOpen) return null;

  const recordFor = (type: string): ConsentRecord | undefined =>
    records?.find((r) => r.consentType === type);

  const handleToggle = async (type: string, nextGiven: boolean) => {
    setPendingType(type);
    setFeedback(null);
    try {
      await recordConsent({
        consentType: type,
        version: CONSENT_VERSION,
        given: nextGiven,
        method: 'web',
      }).unwrap();

      let refreshed = records;
      if (user?._id) {
        refreshed = await getConsentRecords({ userId: user._id }).unwrap();
      }

      const allSigned = CONSENT_TYPES.every((t) => {
        const record = refreshed?.find((r) => r.consentType === t.value);
        return Boolean(record?.given && !record?.withdrawnAt);
      });

      if (allSigned) {
        localStorage.setItem('consentAccepted', 'true');
      } else {
        localStorage.removeItem('consentAccepted');
      }

      setFeedback(nextGiven ? 'Consent given.' : 'Consent withdrawn.');
    } catch {
      setFeedback('Could not save your choice. Try again.');
    } finally {
      setPendingType(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        onClick={() => setIsOpen(false)}
      />

      {/* Panel */}
      <div className="relative w-full max-w-lg rounded-xl bg-white border-[0.5px] border-[#D4D4D4] shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[#D4D4D4] px-6 py-5">
          <div>
            <h2
              id="consent-modal-title"
              className="text-lg font-semibold text-slate-900"
            >
              Privacy preferences
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Choose what you agree to share and how it&apos;s used.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
          {isFetching && (
            <div className="flex items-center justify-center py-10">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
            </div>
          )}

          {isError && !isFetching && (
            <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              Couldn&apos;t load your consent settings. Please try again later.
            </p>
          )}

          {!isFetching && !isError && (
            <ul className="divide-y divide-[#D4D4D4]">
              {CONSENT_TYPES.map(({ value, label }) => {
                const record = recordFor(value);
                const given = Boolean(record?.given && !record?.withdrawnAt);
                const isRowPending = pendingType === value && isSaving;

                return (
                  <li
                    key={value}
                    className="flex items-center justify-between gap-4 py-4"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {label}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {given
                          ? 'You have given consent.'
                          : 'You have not given consent.'}
                      </p>
                    </div>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={given}
                      disabled={isRowPending}
                      onClick={() => handleToggle(value, !given)}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 ${
                        given
                          ? 'bg-[#6E3EF5] focus-visible:ring-[#6E3EF5]'
                          : 'bg-slate-200 focus-visible:ring-slate-400'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          given ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {feedback && (
            <p
              className={`mt-4 text-sm ${
                feedback.startsWith('Could not')
                  ? 'text-red-600'
                  : 'text-[#6E3EF5]'
              }`}
            >
              {feedback}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[#D4D4D4] px-6 py-4">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 bg-gray-200 transition hover:bg-gray-100"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default Consent;
